import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { RouteDialogContext } from "arena/components/RouteDialog";
import ProxyPageOverrideContext from "arena/components/ProxyPage/ProxyPageOverrideContext";
import Channel from "arena/components/Channel";
import Video from "arena/components/Video";
import Stream from "arena/components/Stream";
import Product from "arena/components/Product";
import Post from "arena/components/Post";
import DigitalAsset from "arena/components/DigitalAsset";
import { useArena } from "core/components/ArenaProvider";
import { useIsRouteDialogOpen } from "arena/components/RouteDialog/RouteDialogOpenContext";

const CHANNEL = "CHANNEL";
const VIDEO = "VIDEO";
const STREAM = "STREAM";

/**
 * Высота шапки арены: Toolbar задаёт minHeight { xs: 48, sm: 64 }, и панель
 * прокси-страницы начинается ровно под ней, без щели и без перекрытия.
 */
const HEADER_HEIGHT = { xs: 48, sm: 64 };

function ProxyPageContent({ arenaProxyPage, suspended }) {
  const { content, product, post, digitalAsset, tag } = arenaProxyPage;

  if (tag) {
    return <Navigate to={new URL(tag.url).pathname} replace />;
  }

  if (content?.type === CHANNEL && content?.channel?.slug) {
    return (
      <Channel params={{ slug: content.channel.slug }} suspended={suspended} />
    );
  }

  if (content?.type === VIDEO) {
    return <Video params={{ contentId: content.id }} suspended={suspended} />;
  }

  if (content?.type === STREAM) {
    return <Stream params={{ contentId: content.id }} suspended={suspended} />;
  }

  if (product) {
    return <Product params={{ productId: product.id }} />;
  }

  if (post) {
    return <Post params={{ postId: post.id }} />;
  }

  if (digitalAsset) {
    return <DigitalAsset params={{ digitalAssetId: digitalAsset.id }} />;
  }

  return null;
}

ProxyPageContent.propTypes = {
  arenaProxyPage: PropTypes.object.isRequired,
  suspended: PropTypes.bool,
};

const noop = () => {};

/**
 * Прокси-страница (матч на sports.ru и прочие внешние адреса) открывается
 * той же выдвижной панелью, что и обычные маршруты контента, и на всех
 * ширинах ведёт себя одинаково.
 *
 * Раньше на мобильных включался полноэкранный режим: панель прижималась к
 * нулю поверх шапки (z-index 1300 против 1200 у шапки) и запрещала
 * прокрутку (overflowY hidden при height 100%). Из-за этого всё, что ниже
 * плеера — описание, табы, похожие видео — на телефоне было недостижимо, а
 * шапка арены не показывалась вовсе. Тот же Channel, открытый обычным
 * маршрутом /tv/:slug, идёт без полноэкранного режима и прокручивается
 * нормально: полный экран в RouteDialog это отдельный флаг, и включает его
 * только вертикальный Shorty, которому он и нужен.
 *
 * Побочно уходит и рассинхрон гидрации: на сервере useMediaQuery всегда
 * отдаёт false, поэтому разметка приезжала обычной, а на клиенте сразу
 * перестраивалась в полноэкранную.
 */
export default function ProxyPageDialog({ arenaProxyPage }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const arena = useArena();
  /**
   * Поверх прокси-страницы может открыться RouteDialog (похожее видео,
   * канал, стрим). Сама прокси-страница при этом остаётся смонтированной,
   * и её плеер продолжал бы играть под диалогом — ставим его на паузу
   * через suspended, как это сделано в ArenaTv.
   */
  const isRouteDialogOpen = useIsRouteDialogOpen();

  const {
    metadataTitle,
    metadataDescription,
    metadataKeywords,
    path,
    overrideTitle,
    overrideDescription,
  } = arenaProxyPage;

  const onClose = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const contextValue = useMemo(() => ({ onClose }), [onClose]);

  const overrideValue = useMemo(
    () => ({
      title: metadataTitle || null,
      description: metadataDescription || null,
      keywords: metadataKeywords || null,
      canonicalUrl: `${arena.url}${path}`,
      displayTitle: overrideTitle || null,
      displayDescription: overrideDescription || null,
    }),
    [
      metadataTitle,
      metadataDescription,
      metadataKeywords,
      arena.url,
      path,
      overrideTitle,
      overrideDescription,
    ]
  );

  return (
    <>
      <SwipeableDrawer
        open
        anchor="bottom"
        onClose={onClose}
        onOpen={noop}
        elevation={0}
        disableSwipeToOpen
        disableDiscovery
        allowSwipeInChildren
        disablePortal
        disableAutoFocus
        disableRestoreFocus
        disableEscapeKeyDown
        keepMounted
        sx={{
          zIndex: 1099,
          top: HEADER_HEIGHT,
          "& > .MuiDrawer-paper": {
            bgcolor: theme.palette.background.body,
            position: "absolute",
            top: 0,
            overflowY: "auto",
            height: "auto",
          },
          "& > .MuiModal-backdrop": {
            bgcolor: theme.palette.background.body,
          },
        }}
      >
        <RouteDialogContext.Provider value={contextValue}>
          <ProxyPageOverrideContext.Provider value={overrideValue}>
            <Box
              sx={{
                overflowY: "auto",
                height: "auto",
                /**
                 * Панель прокручивается внутри себя, а страница под ней
                 * остаётся на месте: без этого на тач-экранах докрутка до
                 * конца списка начинает тянуть фоновую страницу.
                 */
                overscrollBehavior: "contain",
              }}
            >
              <ProxyPageContent
                arenaProxyPage={arenaProxyPage}
                suspended={isRouteDialogOpen}
              />
            </Box>
          </ProxyPageOverrideContext.Provider>
        </RouteDialogContext.Provider>
      </SwipeableDrawer>

      {/* Canonical и meta теперь переопределяются через ProxyPageOverrideContext в ContentHelmet/ProductHelmet */}
    </>
  );
}

ProxyPageDialog.propTypes = {
  arenaProxyPage: PropTypes.object.isRequired,
};
